import { useQueryClient } from "@tanstack/react-query";

import { MetricsNodeStatusCountsResponse } from "src/api/types";
import { NODE_TYPE } from "src/constants/NodeTypes";
import {
  useIsJobRunMetricsWritten,
  useJobRunCountsForNode,
  useOutputNodeJobRunCounts,
} from "src/flowGraph/useJobRunCounts";
import { getJobRunFlags, JobRun } from "src/jobs/types";
import { metricsQueryKeys } from "src/performance/queries";
import { useGraphStore } from "src/store/StoreProvider";

export const useJobRunNodeStatusCounts = (jobRun: JobRun) => {
  const { selectedNode } = useGraphStore();
  const queryClient = useQueryClient();

  const { isJobRunning } = getJobRunFlags(jobRun.status.type);

  const isOutputNode = selectedNode?.type === NODE_TYPE.OUTPUT_NODE;
  const isMetricsWritten = useIsJobRunMetricsWritten();

  const perNodeCounts = useJobRunCountsForNode(selectedNode?.id ?? "");
  const outputCounts = useOutputNodeJobRunCounts();
  const jobRunCounts = isOutputNode ? outputCounts : perNodeCounts;

  const totalCount = (() => {
    if (isOutputNode) {
      return outputCounts?.total ?? 0;
    }

    if (!perNodeCounts) {
      return 0;
    }

    return (
      perNodeCounts.success +
      perNodeCounts.failed +
      perNodeCounts.ignored +
      perNodeCounts.pending +
      perNodeCounts.cancelled +
      perNodeCounts.other
    );
  })();

  const isPolling =
    queryClient.getQueryState<MetricsNodeStatusCountsResponse>(
      metricsQueryKeys.nodeStatusCounts(jobRun.id),
    )?.fetchStatus === "fetching";

  return {
    isJobRunning,
    isPolling,
    isSkipped: typeof perNodeCounts === "undefined" && isMetricsWritten,
    successCount: jobRunCounts?.success ?? 0,
    failureCount: jobRunCounts?.failed ?? 0,
    ignoredCount: jobRunCounts?.ignored ?? 0,
    outputMismatchCount: jobRunCounts?.output_mismatch ?? 0,
    totalCount,
  };
};
